/**
 * 数字雨渲染引擎。
 *
 * 三条渲染路径（Worker / 主线程回退）共用这里的绘制逻辑，保证画出来完全一致：
 * - 启动层（不透明黑底）与首页背景层（透明底）画的是同一场雨，位置/速度/颜色共享。
 * - 谁最早挂载谁是 driver，只有 driver 推进雨滴位置与行波相位，避免两层各推进一次导致速度翻倍。
 *
 * 性能上的关键点：字符不用 fillText + shadowBlur 逐帧画。
 * 光晕（shadowBlur）是 Canvas 2D 里最慢的路径之一，一屏近百列每帧画一次，
 * 在手机上单帧就能吃掉十几毫秒，主线程稍一忙数字流就跟着卡。
 * 现在改成启动时把「亮字 + 彩色光晕」预烘焙成字形图集，逐帧只做 drawImage。
 */

// 数字雨字形集合：片假名 + 拉丁字母 + 数字 + 符号
export const MATRIX_GLYPHS =
  'アカサタナハマヤラワガザダバパイキシチニヒミリウクスツヌフムユルエケセテネヘメレオコソトノホモヨロABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%&*+=<>';

// 青蓝色调，逐列随机取色形成层次
export const MATRIX_COLORS = ['#22d3ee', '#38bdf8', '#0ea5e9', '#2dd4bf', '#67e8f9'];

// 头字颜色（最亮）与光晕强度
const RAIN_HEAD_COLOR = '#e6feff';
const RAIN_GLOW_BLUR = 14;

// 半透明黑幕的不透明度：值越小拖影残留越长
export const MATRIX_TRAIL_FADE = 0.02;

// 融入背景后开始减速的时间与幅度：等交接完成（620ms）再起步，用 2s 慢慢降到 18%
export const RAIN_SLOWDOWN_DELAY_MS = 620;
export const RAIN_SLOWDOWN_DURATION_MS = 2000;
export const RAIN_SLOWDOWN_MIN_SCALE = 0.18;

// 融入背景后的「背景化」处理：拖影更短、头字光晕更弱
export const RAIN_BACKGROUND_TRAIL_SCALE = 1.75;
export const RAIN_BACKGROUND_GLOW = 7;
export const RAIN_BACKGROUND_RAMP_MS = 240;

// 融入背景后字符逐渐缩小
export const RAIN_BACKGROUND_SHRINK_DURATION_MS = 2000;
export const RAIN_BACKGROUND_MIN_GLYPH = 0.55;

// 首页出现时的波动：以列为单位横向传播的正弦行波
export const RAIN_WAVE_WAVELENGTH_COLUMNS = 9;
export const RAIN_WAVE_PERIOD_MS = 1100;
export const RAIN_WAVE_DELAY_MS = 120;
export const RAIN_WAVE_MAX_OFFSET = 1.1;
export const RAIN_WAVE_RAMP_MS = 320;

// 单帧最大步进：主线程偶尔被长任务占住时，限制追赶幅度，避免雨后瞬间跳一大段
export const RAIN_MAX_FRAME_DELTA_MS = 120;

export interface RainState {
  // 每列雨头当前所在行（可为负，表示还在屏幕上方）
  drops: number[];
  speeds: number[];
  colors: string[];
  // 整体下落速度系数：数字流融入首页背景后会被逐渐调小
  speedScale: number;
  // 横穿屏幕的行波相位与振幅（相位由 driver 推进，振幅由背景层给出）
  wavePhase: number;
  waveAmplitude: number;
}

export interface RainLayer {
  // 不透明层 = 启动层（黑底）；透明层 = 首页背景层
  opaque: boolean;
  width: number;
  height: number;
  ratio: number;
  fontSize: number;
  // 透明层挂载时刻，用来推进「融入背景」的减速 / 缩小 / 波动曲线
  backgroundStart: number;
}

export const createRainState = (): RainState => ({
  drops: [],
  speeds: [],
  colors: [],
  speedScale: 1,
  wavePhase: 0,
  waveAmplitude: 0,
});

export const resolveFontSize = (width: number): number => (width < 640 ? 13 : 16);

/**
 * 按列数重新撒一遍雨滴。只有列数变化（尺寸/方向变化）时才调用，
 * 否则沿用已有状态，让启动层的雨无缝接到首页背景层。
 */
export const seedRain = (state: RainState, columnCount: number, rows: number): void => {
  if (state.drops.length === columnCount) return;
  state.drops = Array.from({ length: columnCount }, () => Math.random() * -rows);
  state.speeds = Array.from({ length: columnCount }, () => 0.55 + Math.random() * 0.95);
  state.colors = Array.from(
    { length: columnCount },
    () => MATRIX_COLORS[Math.floor(Math.random() * MATRIX_COLORS.length)],
  );
};

interface GlyphAtlas {
  // 主线程用 HTMLCanvasElement，Worker 里用 OffscreenCanvas，都是可 drawImage 的源
  surface: CanvasImageSource;
  // 每个字形在行内的宽度（含光晕外扩），单位：设备像素
  cell: number;
  // 光晕外扩边距，单位：设备像素
  pad: number;
}

const atlasCache = new Map<string, GlyphAtlas>();

const createSurface = (width: number, height: number): HTMLCanvasElement | OffscreenCanvas => {
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(width, height);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const getGlyphAtlas = (color: string, fontSize: number, ratio: number): GlyphAtlas | null => {
  const key = `${color}|${fontSize}|${ratio}`;
  const cached = atlasCache.get(key);
  if (cached) return cached;

  const glyphSize = fontSize * ratio;
  const pad = Math.ceil(RAIN_GLOW_BLUR * ratio * 0.6) + 2;
  const cell = Math.ceil(glyphSize * 1.25) + pad * 2;
  const surface = createSurface(cell * MATRIX_GLYPHS.length, cell);
  const ctx = (surface as HTMLCanvasElement).getContext('2d') as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (!ctx) return null;

  ctx.font = `${glyphSize}px "SFMono-Regular", Menlo, Consolas, monospace`;
  ctx.textBaseline = 'top';
  ctx.fillStyle = RAIN_HEAD_COLOR;
  ctx.shadowColor = color;
  ctx.shadowBlur = RAIN_GLOW_BLUR * ratio;
  for (let i = 0; i < MATRIX_GLYPHS.length; i += 1) {
    ctx.fillText(MATRIX_GLYPHS.charAt(i), i * cell + pad, pad);
  }

  const atlas: GlyphAtlas = { surface, cell, pad };
  atlasCache.set(key, atlas);
  return atlas;
};

/** 预热字形图集，避免第一帧临时烘焙造成可见的停顿 */
export const warmUpGlyphAtlases = (fontSize: number, ratio: number): void => {
  for (const color of MATRIX_COLORS) {
    getGlyphAtlas(color, fontSize, ratio);
  }
};

export interface RenderFrameOptions {
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;
  state: RainState;
  layer: RainLayer;
  // 当前时间戳（performance.now 口径）
  timestamp: number;
  // 与上一帧的间隔（已夹取上限）
  delta: number;
  isDriver: boolean;
}

/** 画一帧数字雨。Worker 与主线程回退路径都调用它。 */
export const renderRainFrame = ({
  ctx,
  state,
  layer,
  timestamp,
  delta,
  isDriver,
}: RenderFrameOptions): void => {
  const { width, height, fontSize, opaque } = layer;
  if (width <= 0 || height <= 0) return;

  const step = delta / 16.67;

  if (isDriver) {
    state.wavePhase = (state.wavePhase + (delta / RAIN_WAVE_PERIOD_MS) * Math.PI * 2) % (Math.PI * 2);
  }

  // 首页背景层负责减速，并给出「背景化」程度与字符缩放
  let backgroundLook = 0;
  let glyphScale = 1;
  if (!opaque) {
    const sinceMount = timestamp - layer.backgroundStart;
    const elapsed = sinceMount - RAIN_SLOWDOWN_DELAY_MS;
    const progress = elapsed <= 0 ? 0 : Math.min(1, elapsed / RAIN_SLOWDOWN_DURATION_MS);
    const eased = 1 - Math.pow(1 - progress, 3);
    state.speedScale = 1 - (1 - RAIN_SLOWDOWN_MIN_SCALE) * eased;
    backgroundLook = Math.max(0, Math.min(1, elapsed / RAIN_BACKGROUND_RAMP_MS));
    const shrinkProgress = elapsed <= 0 ? 0 : Math.min(1, elapsed / RAIN_BACKGROUND_SHRINK_DURATION_MS);
    const shrinkEased = 1 - Math.pow(1 - shrinkProgress, 2);
    glyphScale = 1 - (1 - RAIN_BACKGROUND_MIN_GLYPH) * shrinkEased;
    const waveElapsed = sinceMount - RAIN_WAVE_DELAY_MS;
    const waveIn = Math.max(0, Math.min(1, waveElapsed / RAIN_WAVE_RAMP_MS));
    const waveDecay = Math.max(0, Math.min(1, (shrinkProgress - 0.3) / 0.7));
    state.waveAmplitude = (1 - Math.pow(1 - waveIn, 2)) * (1 - waveDecay);
  }

  // 拖尾：不透明版叠半透明黑幕，透明版把旧字符擦淡。
  // 两者用同一个衰减系数——启动层和背景层画出来的拖尾长度必须一致。
  const trailFade = MATRIX_TRAIL_FADE * (1 + (RAIN_BACKGROUND_TRAIL_SCALE - 1) * backgroundLook);
  if (opaque) {
    ctx.fillStyle = `rgba(0, 0, 0, ${trailFade})`;
    ctx.fillRect(0, 0, width, height);
  } else {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = `rgba(0, 0, 0, ${trailFade})`;
    ctx.fillRect(0, 0, width, height);
    ctx.globalCompositeOperation = 'source-over';
  }

  const { drops, speeds, colors } = state;
  const waveOffset = RAIN_WAVE_MAX_OFFSET * state.waveAmplitude;
  const columnCount = drops.length;
  // 光晕强弱随背景化收敛；图集是整体缩放的，缩放系数本身也会带上光晕的变化
  const glowScale = glyphScale;

  for (let column = 0; column < columnCount; column += 1) {
    const x = column * fontSize;
    const wave =
      waveOffset === 0
        ? 0
        : Math.sin((column / RAIN_WAVE_WAVELENGTH_COLUMNS) * Math.PI * 2 - state.wavePhase) *
          waveOffset *
          fontSize;
    const y = drops[column] * fontSize + wave;
    const glyphIndex = Math.floor(Math.random() * MATRIX_GLYPHS.length);
    const atlas = getGlyphAtlas(colors[column], fontSize, layer.ratio);

    if (atlas) {
      const drawSize = (atlas.cell * glowScale) / layer.ratio;
      const padOffset = (atlas.pad * glowScale) / layer.ratio;
      ctx.drawImage(
        atlas.surface,
        glyphIndex * atlas.cell,
        0,
        atlas.cell,
        atlas.cell,
        x - padOffset,
        y - padOffset,
        drawSize,
        drawSize,
      );
    } else {
      // 图集不可用（极端情况）时退回直接画字，至少保证内容正确
      ctx.font = `${(fontSize * glyphScale).toFixed(2)}px "SFMono-Regular", Menlo, Consolas, monospace`;
      ctx.textBaseline = 'top';
      ctx.fillStyle = RAIN_HEAD_COLOR;
      ctx.fillText(MATRIX_GLYPHS.charAt(glyphIndex), x, y);
    }

    if (isDriver) {
      drops[column] += speeds[column] * step * state.speedScale;
      // 落到屏幕底部后随机回到顶部重新开始
      if (drops[column] * fontSize > height && Math.random() > 0.972) {
        drops[column] = Math.random() * -12;
      }
    }
  }
};
